// Client/src/hooks/useCheckoutPreview.ts
// Shared, debounced call to POST /billing/checkout-preview — used by both personal
// (BillingPayment.tsx) and team (CreateTeamDialog.tsx) checkout so the tax/discount
// preview logic lives in one place instead of being copied into each component.
import { useEffect, useRef, useState } from 'react';

export interface CheckoutAddress {
  line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

export interface TaxBreakdownEntry {
  display_name: string | null;
  tax_type: string | null;
  percentage: string | null;
  amount: number;
  taxability_reason: string | null;
}

export interface CheckoutPreview {
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total: number;
  currency: string;
  tax_breakdown: TaxBreakdownEntry[];
  reverse_charge: boolean;
  requires_more_info: boolean;
  tax_id_rejected: boolean;
}

export interface UseCheckoutPreviewParams {
  token: string | null | undefined;
  planId: string | undefined;
  billingCycle: 'monthly' | 'yearly';
  workspaceId?: number;
  voucherCode?: string | null;
  customerType: 'individual' | 'business';
  businessName?: string;
  taxIdType?: string;
  taxIdValue?: string;
  address: CheckoutAddress;
  // Which saved card is selected (saved-card checkout only) — the backend uses this to
  // look up that specific card's own billing address rather than a generic customer
  // default. Also matters for reactivity: two saved cards can have different addresses,
  // so this must be a dependency or switching cards silently keeps the stale preview.
  savedPaymentMethodId?: string;
  // Gate the preview off entirely — e.g. the plan is free, or the form isn't ready yet.
  enabled: boolean;
}

const DEBOUNCE_MS = 600;

// Returned when a request never resolves into a real number (network error, non-2xx) — so
// callers can rely on "preview is non-null" to mean "the round trip finished, one way or
// another" instead of a skeleton spinning forever over a request that silently failed.
const UNRESOLVED_PREVIEW: CheckoutPreview = {
  subtotal: 0,
  discount_amount: 0,
  tax_amount: 0,
  total: 0,
  currency: 'usd',
  tax_breakdown: [],
  reverse_charge: false,
  requires_more_info: true,
  tax_id_rejected: false,
};

export function useCheckoutPreview(params: UseCheckoutPreviewParams) {
  const [preview, setPreview] = useState<CheckoutPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const {
    token, planId, billingCycle, workspaceId, voucherCode,
    customerType, businessName, taxIdType, taxIdValue, address, savedPaymentMethodId, enabled,
  } = params;
  const addressKey = JSON.stringify(address);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Deliberately NOT gating on address.country here — saved-card checkout passes an
    // empty address on purpose so the backend falls back to the Stripe customer's address
    // already on file. `enabled` (only true once billing details are submitted) is the
    // real gate; the backend itself handles a genuinely-unresolvable address gracefully
    // (requires_more_info: true) rather than erroring.
    if (!enabled || !token || !planId) {
      setPreview(null);
      return;
    }

    const thisRequestId = ++requestIdRef.current;
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/billing/checkout-preview`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            plan_id: planId,
            billing_cycle: billingCycle,
            workspace_id: workspaceId,
            voucher_code: voucherCode ?? undefined,
            customer_type: customerType,
            business_name: customerType === 'business' ? businessName : undefined,
            tax_id_type: customerType === 'business' ? taxIdType : undefined,
            tax_id_value: customerType === 'business' ? taxIdValue : undefined,
            address,
            saved_payment_method_id: savedPaymentMethodId ?? undefined,
          }),
        });
        if (!response.ok) {
          if (requestIdRef.current === thisRequestId) setPreview(UNRESOLVED_PREVIEW);
          return;
        }
        const data: CheckoutPreview = await response.json();
        if (requestIdRef.current === thisRequestId) {
          setPreview(data);
        }
      } catch {
        // Non-fatal: checkout still works without a preview, just shows the pre-tax total —
        // but still resolves the "are we still waiting" state so a skeleton doesn't spin forever.
        if (requestIdRef.current === thisRequestId) setPreview(UNRESOLVED_PREVIEW);
      } finally {
        if (requestIdRef.current === thisRequestId) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // addressKey is the stable dep for the address object; individual fields covered otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, planId, billingCycle, workspaceId, voucherCode, customerType, businessName, taxIdType, taxIdValue, addressKey, savedPaymentMethodId, enabled]);

  return { preview, loading };
}
